import QRCode from 'qrcodejs2'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { GET } from '@/utils/Request'
import { GET_SUBJECT_CARD } from '@/const/api'
import BarcodeGenerator from '@/utils/BarcodeGenerator'

class SubjectChoiceGenerator {
  constructor() {
    this.isGenerating = false
    this.container = null
  }


  async generateSubjectChoice(unit, quota, onProgress = null, onError = null) {
    if (this.isGenerating) {
      throw new Error('subject choice generation already in progress')
    }

    this.isGenerating = true

    try {
      // Step 1: Fetch subject choice data
      if (onProgress) onProgress('Fetching subject choice data...', 10)
      const subjectInfo = await this.fetchSubjectChoice(unit,quota);

      // Step 2: Pre-load static images
      // if (onProgress) onProgress('Loading resources...', 25)
      // const staticImages = await this.preloadStaticImages()

      // Step 3: Create temporary container
      if (onProgress) onProgress('Preparing subject choice...', 35)
      this.createContainer()

      // Step 4: Render subject choice HTML
      if (onProgress) onProgress('Rendering subject choice...', 50)
      const qrText = `${process.env.VUE_APP_FRONTEND_URL}check-subject-choice/${subjectInfo.hash_key}`
      this.render(subjectInfo, unit, qrText)

      // Step 5: Generate QR code
      if (onProgress) onProgress('Generating QR code...', 60)
      await this.generateQRCodeInContainer(qrText)

      // Step 6: Wait for fonts and images to load
      if (onProgress) onProgress('Loading fonts and images...', 70)
      await this.waitForResourcesLoaded()

      // Step 7: Generate PDF
      if (onProgress) onProgress('Generating PDF...', 85)
      await this.generatePDF(subjectInfo.exam_roll)

      // Step 8: Cleanup
      if (onProgress) onProgress('Download complete!', 100)
      this.cleanup()

      this.isGenerating = false
    } catch (error) {
      this.cleanup()
      this.isGenerating = false
      if (onError) {
        onError(error)
      }
      throw error
    }
  }


  async fetchSubjectChoice(unit, quota) {
  try {
    const response = await GET(`${GET_SUBJECT_CARD}${unit}/${quota}`);
    
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch subject choice data');
    }

    const data = response.data;

    return {
      applicant: data.applicant,
      examResult: data.result,
      unit: data.unit,
      quota: data.quota,
      exam_roll: '' + data.exam_roll,
      application_id: '' + data.application_id,
      selectedSubjects: data.selected_subjects,
      save_choice: data.save_choice,
      hash_key:data.hash_key
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
}

formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

   formatTime(date) {
    let hour = date.getHours()
    const mins = String(date.getMinutes()).padStart(2, '0')
    let amPm = 'AM'

    if (hour > 11) {
      amPm = 'PM'
      if (hour > 12) {
        hour -= 12
      }
    }

    return `${hour}:${mins} ${amPm}`
  }


  createContainer() {
    // Remove existing container if any
    this.cleanup()

    // Create new container
    // A4 dimensions at 96 DPI (standard web DPI)
    const a4Width = 794 // 210mm in pixels
    const a4Height = 1123 // 297mm in pixels

    this.container = document.createElement('div')
    this.container.id = 'subject-choice-generator'
    this.container.style.position = 'absolute'
    this.container.style.left = '-9999px'
    this.container.style.top = '-9999px'
    this.container.style.width = a4Width + 'px'
    this.container.style.height = a4Height + 'px'
    this.container.style.backgroundColor = 'white'
    this.container.style.boxSizing = 'border-box'

    document.body.appendChild(this.container)
  }

  async generateQRCodeInContainer(qrText) {
    return new Promise((resolve) => {
      const qrCodeContainer = this.container.querySelector('#qrCodeUrl')

      if (qrCodeContainer) {
        // Clear any existing content
        qrCodeContainer.innerHTML = ''

        // Create canvas div for QR code
        const canvasDiv = document.createElement('div')
        canvasDiv.id = 'canvas'
        qrCodeContainer.appendChild(canvasDiv)

        // Generate QR code
        new QRCode('canvas', {
          text: qrText,
          width: 100,
          height: 100,
          colorDark: '#000000',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        })

        // Wait for QR code to render
        setTimeout(() => resolve(), 200)
      } else {
        resolve()
      }
    })
  }




  cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }

  

  render(subjectInfo, qrText) {
    const { applicant, examResult, unit, quota, exam_roll, application_id, selectedSubjects } = subjectInfo
    const unitUpper = unit.toUpperCase()

    const html = `
<style>
@import url('https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&family=Tiro+Bangla:ital@0;1&family=Victor+Mono:ital,wght@0,500;1,500&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-size: 12px;
  line-height: 1.3;
  color: black;
}

.bangla-text { font-family: "Tiro Bangla", serif; font-weight: 400; }
.bangla-text-bold { font-family: "Tiro Bangla", serif; font-weight: 500; }
.english-text { font-family: "Roboto Condensed", sans-serif; font-weight: 400; }
.english-text-bold { font-family: "Roboto Condensed", sans-serif; font-weight: 700; }

.container { width: 100%; padding: 10px 40px 10px 40px; margin: auto; }
.row { display: flex; flex-wrap: wrap; margin: 0; }
.col-md-2, .col-md-6, .col-md-8, .col-md-12 { padding: 0 5px; margin-bottom: 5px; }
.col-md-2 { flex: 0 0 16.6667%; max-width: 16.6667%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
.col-md-8 { flex: 0 0 66.6667%; max-width: 66.6667%; }
.col-md-12 { flex: 0 0 100%; max-width: 100%; }

.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }
.font-weight-bold { font-weight: bold; }

.university-name {
  font-size: 28px;
  line-height: 1.4;
  margin-bottom: 2px;
}
.admission-title { font-size: 12px; margin-bottom: 5px; }
.university-logo { max-width: 100%;height: auto;max-height: 105px; }

.roll-number-section {
  font-size: 14px;
  color: black;
}

.application-id-section {
  font-size: 14px;
  color: black;
  text-align: right;
}

.qrcode { width: 80px; height: 80px; margin: auto; }

.card { border: none; margin-bottom: 8px; padding: 0; background:transparent !important; }
.card-header {
  background-color: #343a40;
  color: #fff;
  font-weight: bold;
  padding: 5px 10px;
  font-size: 12px;
}
.card-body { padding: 5px 10px; }

.table th,
.table td {
  padding: 2px 4px; /* reduced from 3px-5px */
  border: none;     /* no border */
  text-align: left;
  font-size: 12px;  /* smaller font for compactness */
}

.table th {
  background-color: #f1f1f1;
  font-weight: bold;
}

.table-info td {
  background-color: #eef7fb;
}

.count-desc { font-family: 'Times New Roman', Times, serif; font-weight: bold; }
.roll-digit { border: 1px solid black; padding: 1px 4px; font-size: 12px; display: inline-block; margin-right: 2px; }
.roll-digit-last { border-right: 1px solid black; }
span[style*="border: 2px solid black"] { display: inline-block; }

p { margin: 2px 0; }

.subject-choice-header {
  display: flex;
  flex-direction: column; /* stack items vertically */
  justify-content: center; /* center vertically (optional) */
  align-items: center;     /* center horizontally */
  gap: 5px; /* space between title and badge */
  margin-top: 2px;
  margin-bottom: 5px;
}

.unit-quota-badge {
  border: 1px solid black;
  border-radius: 5px;
  padding: 2px 6px;
  font-size: 12px;
  display: inline-block;
}

/* Container rows */
.subject-choice-row {
  display: flex;
  flex-wrap: nowrap;
  border-bottom: 1px solid #ddd;
  padding: 4px 0;
}

/* Header row */
.subject-choice-header-row {
  background-color: #eef7fb;
  font-weight: bold;
}

/* Cells */
.subject-choice-cell {
  padding: 2px 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.reguler-row{
  background-color: transparent !important;
}

/* Specific column widths */
.code-cell { flex: 0 0 25%; text-align: center; }
.title-cell { flex: 0 0 50%; text-align: left; }
.serial-cell { flex: 0 0 25%; text-align: center; }

/* Optional styling for card body */
.card-body {
  padding: 5px 10px;
}

.applicant-info-row {
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: 4px;
  font-size: 12px;
}

.applicant-info-label {
  flex: 0 0 30%; /* adjust width of label column */
  font-weight: bold;
}

.applicant-info-separator {
  flex: 0 0 5%;  /* small space for ':' */
  text-align: center;
}

.applicant-info-value {
  flex: 1 0 65%; /* remaining width */
}

/* Watermark styles */
.subject-choice-generator-wrapper {
  position: relative;
  overflow: hidden; /* Ensure watermark doesn't spill out */
  z-index: 1;
}

.subject-choice-generator-wrapper::before {
  content: '';
  position: absolute;
  top: 65%;
  left: 50%;
  width: 100%; /* Adjust size as needed */
  height: 100%; /* Adjust size as needed */
  background-image: url('https://cu-admission.sgp1.cdn.digitaloceanspaces.com/logo/cu_watermark.png');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 50%; /* Adjust size of the watermark */
  opacity: 0.7; /* Adjust opacity for watermark effect */
  transform: translate(-50%, -50%); /* Center the watermark */
  pointer-events: none; /* Allow interaction with content above */
  z-index: 0 !important; /* Place it behind the content */
}


/* Force A4 page size for print */
@page { size: A4; margin: 15mm 10mm; }
</style>

<div class="subject-choice-generator-wrapper">
<section class="event team-sec team-grid speaker set-relative py-5" id="speaker">
  <div class="container">

    <!-- Header -->
    <div class="row">
      <div class="col-md-2 text-center">
        <img alt="Logo" class="university-logo" src="https://cu-admission.sgp1.digitaloceanspaces.com/logo/admit_logo.png">
      </div>
      <div class="col-md-8 text-center">
        <div class="bangla-text-bold university-name">চট্টগ্রাম বিশ্ববিদ্যালয়</div>
        <div class="bangla-text admission-title">১ম বর্ষ স্নাতক (সম্মান) ভর্তি পরীক্ষা, শিক্ষাবর্ষ: ২০২৫-২০২৬</div>
      </div>
      <div class="col-md-2 text-center">
        <div class="qrcode" id="qrCodeUrl"></div>
      </div>
    </div>

    <!-- Subject-Choice Info -->
    <div class="row">
      <div class="col-md-12 text-center subject-choice-header">
        <div class="english-text-bold" style="font-size: 16px; margin:0;">Subject-Choice List</div>
        <div class="english-text unit-quota-badge">
          Unit: <b>${unitUpper}</b> &nbsp; Quota: <b>${quota}</b>
        </div>
      </div>
    </div>

    <!-- Roll & Application -->
    <div class="row">
      <div class="col-md-6">
        <div class="english-text roll-number-section">Admission Roll: <b>${this.renderDigits(exam_roll)}</b></div>
      </div>
      <div class="col-md-6 text-right">
        <div class="english-text application-id-section">Application ID: <b>${this.renderDigits(application_id)}</b></div>
      </div>
    </div>

    <!-- Applicant Info -->
    <div class="row">
      <div class="col-md-12">
        <div class="card english-text">
          <div class="card-header">Applicant's Info</div>
          

          <div class="card-body">

            <div class="applicant-info-row">
              <div class="applicant-info-label">Name</div>
              <div class="applicant-info-separator">:</div>
              <div class="applicant-info-value english-text">${applicant.name}</div>
            </div>

            <div class="applicant-info-row">
              <div class="applicant-info-label">Father's Name</div>
              <div class="applicant-info-separator">:</div>
              <div class="applicant-info-value english-text">${applicant.fname}</div>
            </div>

            <div class="applicant-info-row">
              <div class="applicant-info-label">Mother's Name</div>
              <div class="applicant-info-separator">:</div>
              <div class="applicant-info-value english-text">${applicant.mname}</div>
            </div>

            <div class="applicant-info-row">
              <div class="applicant-info-label">Total Score</div>
              <div class="applicant-info-separator">:</div>
              <div class="applicant-info-value english-text"><b>${examResult.total}</b></div>
            </div>

            <div class="applicant-info-row">
              <div class="applicant-info-label">Merit Position</div>
              <div class="applicant-info-separator">:</div>
              <div class="applicant-info-value english-text"><b>${examResult.merit}</b></div>
            </div>

          </div>



        </div>

        
        <div class="card mt-1 english-text">
          <div class="card-header">Subject-Choice List</div>
          <div class="card-body">

            <!-- Table header -->
            <div class="subject-choice-row subject-choice-header-row">
              <div class="subject-choice-cell code-cell">Department Code</div>
              <div class="subject-choice-cell title-cell">Name of the Department/Institute</div>
              <div class="subject-choice-cell serial-cell">Choice Serial</div>
            </div>

            <!-- Table rows -->
            ${selectedSubjects.map(sub => `
            <div class="subject-choice-row" style="background: transparent !important;">
              <div class="subject-choice-cell code-cell"><b>${sub.code}</b></div>
              <div class="subject-choice-cell title-cell">${sub.title}</div>
              <div class="subject-choice-cell serial-cell"><b>${sub.serial}</b></div>
            </div>
            `).join('')}

          </div>
        </div>



      </div>
    </div>

  </div>
</section>
</div>
`;


    
    this.container.innerHTML = html;
  }

  renderDigits(number) {
    const digits = String(number).split('')
    return digits.map((digit, index) => {
      const isLast = index === digits.length - 1
      return `<span class="roll-digit ${isLast ? 'roll-digit-last' : ''}">${digit}</span>`
    }).join('')
  }

  /**
   * Wait for all resources (fonts, images) to load
   */
  async waitForResourcesLoaded() {
    // Load font
    await document.fonts.load('16px "Tiro Bangla"')

    // Wait for all images to load
    const images = this.container.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) {
          return Promise.resolve()
        }
        return new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = () => {
            console.warn('Image failed to load:', img.src)
            resolve() // Resolve anyway to not block
          }
          // Timeout after 5 seconds
          setTimeout(resolve, 5000)
        })
      })
    )

    // Optimize images by reducing their display size
    //await this.optimizeImages()

    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 300))
  }
  /**
   * Optimize images for smaller PDF size
   */
  async optimizeImages() {
    const images = this.container.querySelectorAll('img')

    for (const img of images) {
      // Skip if image failed to load
      if (!img.complete || !img.naturalWidth) continue

      try {
        // Create a canvas to resize and compress the image
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        // Get current display dimensions
        const rect = img.getBoundingClientRect()
        const maxWidth = rect.width * 1.5 // 1.5x for retina displays
        const maxHeight = rect.height * 1.5

        // Calculate new dimensions maintaining aspect ratio
        let width = img.naturalWidth
        let height = img.naturalHeight

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        canvas.width = width
        canvas.height = height

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)
      } catch (error) {
        console.warn('Failed to optimize image:', error)
        // Continue with original image if optimization fails
      }
    }
  }

  /*

  async generatePDF(examRoll) {
    const element = this.container.querySelector('.subject-choice-generator-wrapper')
    //const element = this.container

    // Render to canvas with optimized settings
    const canvas = await html2canvas(element, {
      scale: 1.5, // Balanced quality vs size (was 2)
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      removeContainer: true,
      imageTimeout: 0
    })

    // Convert to JPEG with compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.85) // 85% quality JPEG

    // Create PDF with compression
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true // Enable PDF compression
    })

    // Calculate dimensions to fit A4
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width

    // Add image with JPEG format (smaller than PNG)
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST')

    // Download PDF
    const filename = `cu_subject_choice_${examRoll}.pdf`
    pdf.save(filename)
  }
  */

  async generatePDF(examRoll) {
  const element = this.container.querySelector('.subject-choice-generator-wrapper');

  // Render the whole content to a canvas
  const canvas = await html2canvas(element, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#ffffff'
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.85);
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imgProps = pdf.getImageProperties(imgData);
  const pdfImgHeight = (imgProps.height * pdfWidth) / imgProps.width;

  let position = 0;
  let remainingHeight = pdfImgHeight;

  // Add pages
  while (remainingHeight > 0) {
    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfImgHeight);
    remainingHeight -= pdfHeight;
    position -= pdfHeight;

    if (remainingHeight > 0) {
      pdf.addPage();
    }
  }

  // ----------- Add Footer to last page -----------
  pdf.setPage(pdf.getNumberOfPages()); // go to last page
  const footerText = 'Powered by ICT Cell, University of Chittagong';
  pdf.setFontSize(10);
  pdf.setFont('Roboto Condensed', 'italic');

  // Position footer text at bottom left (10mm from bottom)
  pdf.text(footerText, 15, pdfHeight - 10);

  // Generate and add barcode at bottom right
  try {
    const barcodeDataURL = BarcodeGenerator.generateBarcodeDataURL(examRoll, {
      width: 3,
      height: 40,
      format: 'CODE128'
    });
    
    // Position barcode at bottom right (10mm from right edge, 12mm from bottom)
    const barcodeWidth = 60; // Width in mm
    const barcodeHeight = 14; // Height in mm
    const barcodeX = pdfWidth - barcodeWidth - 10; // 10mm from right
    const barcodeY = pdfHeight - 18; // 18mm from bottom
    
    pdf.addImage(barcodeDataURL, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
  } catch (error) {
    console.warn('Failed to generate barcode:', error);
    // Continue without barcode if generation fails
  }

  // Save PDF
  const filename = `cu_subject_choice_${examRoll}.pdf`;
  pdf.save(filename);
  // instead of save — create blob + URL
  //const pdfBlob = pdf.output('blob');
  //const pdfUrl = URL.createObjectURL(pdfBlob);

  // open outside your project
  //window.open(pdfUrl, "_blank");

}


  cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }
}

export default new SubjectChoiceGenerator()
